import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import {
  fantasy,
  gameStatusMessages,
  glossary,
} from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import {
  isGameFinished,
  isGameLive,
} from '@sorare/marketplace/src/shared/game/lib';

import { getCompetitionPictureUrl, getTeamName } from '#src/lib/so5.ts';

import type { HorizontalGame_anyGameInterface } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  padding: var(--double-unit) 0;
  border-bottom: 1px solid var(--c-nd-150);
`;
const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr minmax(0, 1fr) 100px minmax(0, 1fr) 1fr;
  gap: var(--double-unit);
  transition: opacity 0.2s ease-in-out;
  align-items: center;

  &:hover:has(button${LinkOverlay}) {
    opacity: 0.5;
  }

  &.reversed {
    > *:nth-child(2) {
      grid-column: 4;
      grid-row: 1;
    }
    > *:nth-child(4) {
      grid-column: 2;
      grid-row: 1;
    }
  }
`;
const CompetitionLogo = styled.img`
  object-fit: contain;
  opacity: 0.5;
`;
const StyledHorizontal = styled(Horizontal)`
  justify-content: end;
`;
const Scores = styled(Horizontal)`
  .reversed & {
    flex-direction: row-reverse;
  }
`;

type Props = {
  game: HorizontalGame_anyGameInterface;
  onClick?: () => void;
  right?: ReactNode;
  footer?: ReactNode;
};

export const HorizontalGame = ({ game, onClick, right, footer }: Props) => {
  const { formatMessage } = useIntl();

  const reverseTeamOrder =
    isType(game, 'GameOfBaseball') || isType(game, 'GameOfBasketball');

  const live = isGameLive(game.status);
  const finished = isGameFinished(game.status);

  const competitionPictureUrl = getCompetitionPictureUrl(game.competition);

  return (
    <LinkBox>
      <Root gap={2}>
        <Grid
          className={classNames({
            reversed: reverseTeamOrder,
          })}
        >
          <Horizontal>
            {!!competitionPictureUrl && (
              <CompetitionLogo
                src={competitionPictureUrl}
                alt={game.competition.displayName}
                width="32px"
                height="32px"
              />
            )}
          </Horizontal>
          <StyledHorizontal gap={2}>
            <BodyM as="p" bold>
              <Ellipsis>{getTeamName(game.homeTeam)}</Ellipsis>
            </BodyM>
            <TeamAvatar team={game.homeTeam} />
          </StyledHorizontal>
          <LinkOverlay as={onClick ? ButtonBase : 'div'} onClick={onClick}>
            <Vertical center gap={0.5}>
              {live || finished ? (
                <Scores as="span" center gap={0.5}>
                  <BodyM
                    color={
                      live || finished ? 'var(--c-white)' : 'var(--c-nd-600)'
                    }
                    bold={game.homeScore > game.awayScore}
                  >
                    {game.homeScore}
                  </BodyM>
                  <BodyM
                    color={
                      live || finished ? 'var(--c-white)' : 'var(--c-nd-600)'
                    }
                  >
                    -
                  </BodyM>
                  <BodyM
                    color={
                      live || finished ? 'var(--c-white)' : 'var(--c-nd-600)'
                    }
                    bold={game.awayScore > game.homeScore}
                  >
                    {game.awayScore}
                  </BodyM>
                </Scores>
              ) : (
                <FormattedDate
                  value={game.date}
                  hour="numeric"
                  minute="numeric"
                />
              )}
            </Vertical>
          </LinkOverlay>
          <Horizontal gap={2}>
            <TeamAvatar team={game.awayTeam} />
            <BodyM as="p" bold>
              <Ellipsis>{getTeamName(game.awayTeam)}</Ellipsis>
            </BodyM>
          </Horizontal>
          <StyledHorizontal>
            {right || (
              <>
                {!!live && (
                  <Horizontal gap={0.5}>
                    <LiveDot
                      animate
                      size="sm"
                      label={formatMessage(fantasy.live)}
                    />
                    <LabelS as="span" bold color="var(--c-red-400)">
                      {isType(game, 'Game') ? (
                        <>{game.minute}&apos;</>
                      ) : (
                        <>
                          {isType(game, 'GameOfBasketball') ? (
                            <>
                              {game.isHalftime ? (
                                <FormattedMessage
                                  id="Game.status.halftime"
                                  defaultMessage="Half"
                                />
                              ) : (
                                <FormattedMessage
                                  {...glossary.ordinal}
                                  values={{ ordinal: game.quarter }}
                                />
                              )}
                            </>
                          ) : (
                            <FormattedMessage {...gameStatusMessages.live} />
                          )}
                        </>
                      )}
                    </LabelS>
                  </Horizontal>
                )}
                {!!finished && (
                  <LabelS bold color="var(--c-nd-600)" className="text-center">
                    <FormattedMessage {...fantasy.fullTime} />
                  </LabelS>
                )}
              </>
            )}
          </StyledHorizontal>
        </Grid>
        {footer}
      </Root>
    </LinkBox>
  );
};

HorizontalGame.fragments = {
  anyGameInterface: gql`
    fragment HorizontalGame_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      date
      ... on Game {
        id
        minute
      }
      ... on GameOfBasketball {
        id
        isHalftime
        quarter
      }
      competition {
        slug
        displayName
        ...getCompetitionPictureUrl_competition
      }
      homeScore
      awayScore
      homeTeam {
        slug
        ...TeamAvatar_team
        ...getTeamName_team
      }
      awayTeam {
        slug
        ...TeamAvatar_team
        ...getTeamName_team
      }
    }
    ${TeamAvatar.fragments.team}
    ${getTeamName.fragments.team}
    ${getCompetitionPictureUrl.fragments.competition}
  ` as TypedDocumentNode<HorizontalGame_anyGameInterface>,
};
